import React from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import styled from 'styled-components'
import { IconChevronDown } from 'components/Icons'
import SkeletonLiveCard from 'components/Skeleton/SkeletonLiveCard'
import {
  BROADCAST_LIST,
  IS_PREV_BROADCAST_DATA_EXIST,
  MORE_BROADCAST_LIST_INDEX,
  PREV_BROADCAST_LIST_INDEX,
  currentDate,
} from 'core/store/broadcastTable'
import useTodayBroadcastList from 'core/hooks/useTodayBroadcastList'
import BroadcastCard from './BroadcastCard'
import { useTranslation } from 'react-i18next'

interface TodayBroadcastListProps {
  layoutEl: HTMLDivElement | null
}

const TodayBroadcastList: React.FC<TodayBroadcastListProps> = ({ layoutEl }) => {
  const isPrevDataExist = useRecoilValue(IS_PREV_BROADCAST_DATA_EXIST)
  const broadcastItem = useRecoilValue(BROADCAST_LIST(currentDate))
  const [prevListIndex, setPrevListIndex] = useRecoilState(PREV_BROADCAST_LIST_INDEX)
  const [moreListIndex, setMoreListIndex] = useRecoilState(MORE_BROADCAST_LIST_INDEX)

  const { fromNow, past } = useTodayBroadcastList()
  const { t } = useTranslation()

  /**
   * 지난 라이브 버튼 열기 / 닫기 출력 여부
   */
  const isOpenablePrevList = prevListIndex > 0
  /**
   * 지난 라이브 목록 출력 여부
   */
  const isShowPrevList = prevListIndex >= 0
  /**
   * 더보기 방송 출력 여부
   */
  const isShowMoreList = moreListIndex > 1

  /**
   * 더보기 버튼 클릭 핸들러
   *
   * 더보기 버튼 클릭 시 moreListIndex가 1씩 감소
   */
  const handleClickMoreButton = () => {
    setMoreListIndex((prevIndex) => prevIndex - 1)
  }

  /**
   * 지난 라이브 열기 / 닫기 버튼 클릭 핸들러
   */
  const handleClickPrevButton = () => {
    /**
     * 지난 라이브 닫기 클릭 시 prevListIndex 초기화
     */
    if (!isOpenablePrevList) {
      setPrevListIndex(broadcastItem.past.length)
      return
    }

    /**
     * 지난 라이브 열기 클릭 시 prevListIndex가 1씩 감소
     */
    if (isOpenablePrevList) {
      setPrevListIndex((prev) => prev - 1)
    }
  }

  return (
    <>
      {isPrevDataExist && (
        <StyledButton isOpenablePrevList={isOpenablePrevList} onClick={handleClickPrevButton}>
          {`${t('prev_broadcast')} ${isOpenablePrevList ? t('open') : t('close')}`}
          <IconChevronDown />
        </StyledButton>
      )}
      <StyledBroadcastCardWrapper>
        {isShowPrevList && (
          <React.Suspense fallback={<SkeletonLiveCard layoutEl={layoutEl} />}>
            {past.slice(prevListIndex).map((data) => {
              return data.map((item) => <BroadcastCard key={item.broadcastName} item={item} />)
            })}
          </React.Suspense>
        )}
        <React.Suspense fallback={<SkeletonLiveCard layoutEl={layoutEl} />}>
          {fromNow.slice(0, fromNow.length - moreListIndex + 1).map((data) => {
            return data.map((item) => <BroadcastCard key={item.broadcastName} item={item} />)
          })}
        </React.Suspense>
        {isShowMoreList && <StyledButton onClick={handleClickMoreButton}>{t('more')}</StyledButton>}
      </StyledBroadcastCardWrapper>
    </>
  )
}

const StyledButton = styled.button<{ isOpenablePrevList?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 10px 10px 10px 14px;
  border-radius: 100px;
  border: 1px solid #e6e6e6;
  background: #fff;
  appearance: none;
  outline: none;
  color: #000;
  font-size: 16px;
  font-weight: 700;
  line-height: 125%; /* 20px */
  cursor: pointer;
  gap: 8px;
  ${({ isOpenablePrevList }) => isOpenablePrevList && `svg { transform: rotate(180deg); }`}

  @media screen and (max-width:360px) {
    max-width: 260px;
  }
`

const StyledBroadcastCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

export default TodayBroadcastList
